export type IndustryPresetKey = "horeca" | "beauty" | "fitness" | "retail" | "services";

export type HighlightedTariffId = "starter" | "business" | "pro";

export type IndustrySlide = {
    image: string;
    /** 1 short line under carousel */
    caption: string;
};

export type IndustryKpi = {
    value: string;
    label: string;
};

export interface IndustryPreset {
    id: IndustryPresetKey;
    label: string;
    shortLabel: string;
    /** Niche tail after shared UTP, e.g. «для ресторанов и доставки» */
    titleTail: string;
    /** Full H1 for SEO / a11y fallback */
    title: string;
    /** Optional one-liner under H1; keep empty when visual is enough */
    subtitle: string;
    ctaLabel: string;
    demoBotUrl: string;
    accent: string;
    accentSoft: string;
    /** Phone carousel slides */
    slides: IndustrySlide[];
    /** Browser / site showcase */
    siteTitle: string;
    siteLead: string;
    /** Mini slider inside browser frame (first = default) */
    siteImages: string[];
    /** Real demo URL shown in browser chrome (opens in new tab) */
    siteDemoUrl?: string;
    siteDemoHost?: string;
    siteCta: string;
    /** Optional CRM / ops admin slideshow under the SEO site block */
    crmTitle?: string;
    crmLead?: string;
    crmSlides?: IndustrySlide[];
    /** KPI grid */
    kpis: IndustryKpi[];
    highlightedTariffId: HighlightedTariffId;
    /** Extra line on highlighted tariff badge, e.g. «Управление залом, меню…» */
    tariffBadgeHint?: string;
    calculatorVariant: "marketplace" | "beauty" | "ecommerce" | "services" | "events" | "serviceCompanies";
}

const DEMO_BOT = "https://t.me/tezdemobot?startapp";
const SLIDES = {
    a: "/images/hero-slides/01.webp",
    b: "/images/hero-slides/02.webp",
    c: "/images/hero-slides/03.webp",
    d: "/images/hero-slides/04.webp",
} as const;

export const INDUSTRY_PRESET_ORDER: IndustryPresetKey[] = [
    "horeca",
    "beauty",
    "retail",
    "fitness",
    "services",
];

export const INDUSTRY_PRESETS: Record<IndustryPresetKey, IndustryPreset> = {
    horeca: {
        id: "horeca",
        label: "HoReCa",
        shortLabel: "HoReCa",
        title: "Mini App + сайт и CRM для ресторанов и доставки",
        titleTail: "для ресторанов и доставки",
        subtitle: "",
        ctaLabel: "Попробовать бесплатно",
        demoBotUrl: DEMO_BOT,
        accent: "#f97316",
        accentSoft: "rgba(249, 115, 22, 0.18)",
        slides: [
            { image: "/images/presets/horeca-home.png", caption: "Бронь стола, меню и заказ в одном Mini App" },
            { image: "/images/presets/horeca-booking.png", caption: "Выбор даты и слота без звонка" },
            { image: "/images/presets/horeca-vip.png", caption: "Карточка зала и VIP-комнат" },
            { image: "/images/presets/horeca-cart.png", caption: "Корзина и оформление заказа" },
        ],
        siteTitle: "SEO сайт который продвигает бизнес",
        siteLead: "Лёгкий, эффектный и умный",
        siteImages: ["/images/presets/horeca-site-menu.png", "/images/presets/horeca-site.png"],
        siteDemoUrl: "https://demo3.tezam.by/",
        siteDemoHost: "demo3.tezam.by",
        siteCta: "Хочу такой контур",
        crmTitle: "CRM для бизнеса",
        crmLead: "Залы, меню, техкарты, персонал — в одном окне",
        crmSlides: [
            {
                image: "/images/presets/horeca-crm-hall.png",
                caption: "Залы и столы: карта зала и статусы броней",
            },
            {
                image: "/images/presets/horeca-crm-seating.png",
                caption: "Посадка гостей и заказ блюд по гостям",
            },
            {
                image: "/images/presets/horeca-crm-menu.png",
                caption: "Меню, каталог и техкарты",
            },
            {
                image: "/images/presets/horeca-crm-venue.png",
                caption: "Заведение: залы, KDS, касса и персонал",
            },
        ],
        kpis: [
            { value: "0%", label: "комиссии агрегаторов на прямых заказах" },
            { value: "QR", label: "меню и счёт со стола за секунды" },
            { value: "1 контур", label: "зал, кухня, оплата и CRM" },
            { value: "KDS", label: "заказ сразу на экран кухни" },
            { value: "Live", label: "статусы столов без бумажных чеков" },
            { value: "24/7", label: "приём заказов без отдельного приложения" },
        ],
        highlightedTariffId: "pro",
        tariffBadgeHint: "Управление залом, меню, модификаторы, техкарты",
        calculatorVariant: "marketplace",
    },
    beauty: {
        id: "beauty",
        label: "Beauty",
        shortLabel: "Beauty",
        title: "Mini App + сайт и CRM для салонов красоты",
        titleTail: "для салонов красоты",
        subtitle: "",
        ctaLabel: "Попробовать бесплатно",
        demoBotUrl: DEMO_BOT,
        accent: "#ec4899",
        accentSoft: "rgba(236, 72, 153, 0.18)",
        slides: [
            { image: "/images/presets/beauty-home.png", caption: "Главная: запись, услуги и подборки в Mini App" },
            { image: "/images/presets/beauty-services.png", caption: "Каталог услуг с ценой и длительностью" },
            { image: "/images/presets/beauty-journal.png", caption: "Журнал записей мастера на день" },
            { image: "/images/presets/beauty-bonuses.png", caption: "Бонусы, уровни и задания для клиентов" },
        ],
        siteTitle: "Витрина салона с онлайн-записью",
        siteLead: "Красиво для гостя · удобно для администратора",
        siteImages: ["/images/presets/beauty-site-home.png", "/images/presets/beauty-site-service.png"],
        siteCta: "Хочу запись в Telegram",
        crmTitle: "CRM для бизнеса",
        crmLead: "Записи, косметика, характеристики и виджет записи",
        crmSlides: [
            {
                image: "/images/presets/beauty-crm-records.png",
                caption: "Записи: календарь и расписание мастеров",
            },
            {
                image: "/images/presets/beauty-crm-category.png",
                caption: "Категории товаров и косметики",
            },
            {
                image: "/images/presets/beauty-crm-attributes.png",
                caption: "Характеристики и справочники",
            },
            {
                image: "/images/presets/beauty-crm-widget.png",
                caption: "Виджет записи на сайт",
            },
        ],
        kpis: [
            { value: "−no-show", label: "напоминания до визита в Telegram" },
            { value: "1 клик", label: "запись: мастер, услуга, время" },
            { value: "FIFO", label: "склад списывается с услуги" },
            { value: "Payroll", label: "% и фикс без таблиц" },
            { value: "24ч", label: "типовой перенос базы" },
            { value: "0 app", label: "клиенту не нужно ничего ставить" },
        ],
        highlightedTariffId: "business",
        calculatorVariant: "beauty",
    },
    fitness: {
        id: "fitness",
        label: "Fitness",
        shortLabel: "Fitness",
        title: "Mini App + сайт и CRM для фитнес-клубов",
        titleTail: "для фитнес-клубов",
        subtitle: "",
        ctaLabel: "Попробовать бесплатно",
        demoBotUrl: DEMO_BOT,
        accent: "#2563eb",
        accentSoft: "rgba(37, 99, 235, 0.18)",
        slides: [
            { image: "/images/presets/fitness-home.png", caption: "Клуб: услуги, персонал и магазин в Mini App" },
            { image: "/images/presets/fitness-services.png", caption: "Выбор услуги: персоналки и секции" },
            { image: "/images/presets/fitness-booking.png", caption: "Тренер, дата и слот — без звонка" },
            { image: "/images/presets/fitness-shop.png", caption: "Магазин спортпита в том же приложении" },
            { image: "/images/presets/fitness-promo.png", caption: "Промо и акции для клиентов клуба" },
            { image: "/images/presets/fitness-news.png", caption: "Новости и контент центра" },
        ],
        siteTitle: "Сайт клуба с записью и абонементами",
        siteLead: "Вход без очереди у стойки",
        siteImages: [
            "/images/presets/fitness-site-home.png",
            "/images/presets/fitness-site-services.png",
            "/images/presets/fitness-site-trainer.png",
            "/images/presets/fitness-site-service.png",
        ],
        siteCta: "Хочу СКУД в Telegram",
        crmTitle: "CRM для бизнеса",
        crmLead: "Записи, занятия, СКУД и аналитика",
        crmSlides: [
            {
                image: "/images/presets/fitness-crm-orders.png",
                caption: "Записи и заказы клиентов",
            },
            {
                image: "/images/presets/fitness-crm-services.png",
                caption: "Занятия: услуги, цены и тренеры",
            },
            {
                image: "/images/presets/fitness-crm-skud.png",
                caption: "СКУД / QR: турникет и проходы",
            },
            {
                image: "/images/presets/fitness-crm-analytics.png",
                caption: "Аналитика продаж и остатков",
            },
        ],
        kpis: [
            { value: "QR/NFC", label: "проход по активному абонементу" },
            { value: "−ФОТ", label: "меньше дежурного на входе" },
            { value: "2.0", label: "booking групп и персоналок" },
            { value: "P&L", label: "бар + абонементы в одной картине" },
            { value: "24/7", label: "продление без ресепшн" },
            { value: "1 app", label: "всё в Telegram Mini App" },
        ],
        highlightedTariffId: "pro",
        calculatorVariant: "serviceCompanies",
    },
    retail: {
        id: "retail",
        label: "Ecom",
        shortLabel: "Ecom",
        title: "Mini App + сайт и CRM для интернет-магазинов",
        titleTail: "для интернет-магазинов",
        subtitle: "",
        ctaLabel: "Попробовать бесплатно",
        demoBotUrl: DEMO_BOT,
        accent: "#10b981",
        accentSoft: "rgba(16, 185, 129, 0.18)",
        slides: [
            { image: "/images/presets/ecom-home.png", caption: "Витрина магазина и подборки в Mini App" },
            { image: "/images/presets/ecom-catalog.png", caption: "Каталог с фильтрами и SALE-баннером" },
            { image: "/images/presets/ecom-product.png", caption: "Карточка товара: цена, остаток, в корзину" },
            { image: "/images/presets/ecom-favorites.png", caption: "Избранное и быстрый заказ" },
            { image: "/images/presets/ecom-checkout.png", caption: "Оформление: доставка, оплата, промокод" },
        ],
        siteTitle: "SEO-витрина + Mini App магазин",
        siteLead: "Поиск приводит · Telegram продаёт",
        siteImages: [
            "/images/presets/ecom-site-home.png",
            "/images/presets/ecom-site-catalog.png",
            "/images/presets/ecom-site-product.png",
        ],
        siteCta: "Хочу свой магазин",
        crmTitle: "CRM для бизнеса",
        crmLead: "Товары, группы, доставка и способы оплаты",
        crmSlides: [
            {
                image: "/images/presets/ecom-crm-products.png",
                caption: "Список товаров и остатки",
            },
            {
                image: "/images/presets/ecom-crm-groups.png",
                caption: "Группы товаров и витрина",
            },
            {
                image: "/images/presets/ecom-crm-delivery.png",
                caption: "Доставка: радиус, км и стоимость",
            },
            {
                image: "/images/presets/ecom-crm-payments.png",
                caption: "Способы оплаты при оформлении",
            },
        ],
        kpis: [
            { value: "0 app", label: "покупка без установки из стора" },
            { value: "Свой канал", label: "не только маркетплейс" },
            { value: "Бонусы", label: "кэшбэк и рефералка" },
            { value: "Склад", label: "остатки синхронны с витриной" },
            { value: "SEO", label: "сайт для Яндекса и Google" },
            { value: "1 CRM", label: "клиент и заказ в одном окне" },
        ],
        highlightedTariffId: "business",
        calculatorVariant: "ecommerce",
    },
    services: {
        id: "services",
        label: "Service",
        shortLabel: "Service",
        title: "Mini App + сайт и CRM для сервисных компаний",
        titleTail: "для сервисных компаний",
        subtitle: "",
        ctaLabel: "Попробовать бесплатно",
        demoBotUrl: DEMO_BOT,
        accent: "#7c3aed",
        accentSoft: "rgba(124, 58, 237, 0.18)",
        slides: [
            { image: "/images/presets/service-home.png", caption: "Главная: услуги, товары и быстрая запись" },
            { image: "/images/presets/service-catalog.png", caption: "Каталог категорий: сантехника, электрика, замки" },
            { image: "/images/presets/service-select.png", caption: "Выбор услуги в заявке" },
            { image: "/images/presets/service-card.png", caption: "Карточка услуги с описанием" },
            { image: "/images/presets/service-booking.png", caption: "Бронь: мастер, дата, фото и заявка" },
        ],
        siteTitle: "Сайт услуг с формами и SEO",
        siteLead: "Лиды из поиска сразу в работу",
        siteImages: [
            "/images/presets/service-site-home.png",
            "/images/presets/service-site-catalog.png",
            "/images/presets/service-site-service.png",
        ],
        siteCta: "Хочу поток заявок",
        crmTitle: "CRM для бизнеса",
        crmLead: "Чек-листы, специалисты и услуги",
        crmSlides: [
            {
                image: "/images/presets/service-crm-checklists.png",
                caption: "Чек-листы для мастеров и смен",
            },
            {
                image: "/images/presets/service-crm-specialists.png",
                caption: "Специалисты: назначение услуг",
            },
            {
                image: "/images/presets/service-crm-service.png",
                caption: "Редактирование услуги и персонала",
            },
        ],
        kpis: [
            { value: "0 WhatsApp", label: "заявки не теряются в личных чатах" },
            { value: "Предоплата", label: "счёт и чек до старта работ" },
            { value: "Статусы", label: "клиент видит ход в Telegram" },
            { value: "SEO", label: "сайт для Яндекса и Google" },
            { value: "1 поток", label: "от лида до закрытия" },
            { value: "CRM", label: "история и файлы в одном месте" },
        ],
        highlightedTariffId: "starter",
        calculatorVariant: "services",
    },
};

export function resolveVisibleTariffId(id: HighlightedTariffId): "start" | "business" {
    if (id === "starter") return "start";
    return "business";
}
